/**
 * Admin → Billing rules (v2.3.3 time rules + v2.3.5 MILEAGE) — SYSTEM-WIDE.
 *
 * ADMIN ONLY. The UI hides the controls from non-admins, but that is NOT the
 * security boundary: the server rejects PUT /api/billing-rules from any
 * non-admin token with 403. This screen only reads/writes; the rules live in
 * system_settings and are the single source of truth for the tracker app too.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, InputLabel, MenuItem, Select, Stack, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Lock as LockIcon } from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type Rules = { travel: { round: number; min: number }; site: { round: number; min: number } };

interface Payload {
  settings: Record<string, any>;
  defaults: Record<string, any>;
  ranges: Record<string, any>;
  labels: Record<string, string>;
  warnings: string[];
  resolved: { standard: Rules; emergency: Rules };
  formula: string;
  can_edit: boolean;
  mileage?: {
    item_code: string; uom: string; enabled: boolean; rate_per_mile: number; note: string;
  };
  // v2.3.6
  rates?: Record<string, number>;
  phone?: {
    enabled: boolean; rate_per_hour: number; free_minutes: number;
    bill_mode: string; round_min: number; item_code: string; uom: string; note: string;
  };
  on_call?: {
    enabled: boolean; rate_per_hour: number; explicit_rate: number; multiplier: number;
    days: string[]; after_clockout: boolean; holidays: boolean;
    item_code: string; uom: string; note: string;
  };
  items?: Record<string, { uom: string; setting: string }>;
}

const NUM_KEYS = [
  'billing_standard_travel_round_min',
  'billing_standard_travel_min_min',
  'billing_standard_site_min_min',
  'billing_standard_site_round_min',
  'billing_emergency_travel_min_min',
  'billing_emergency_site_min_min',
  'billing_emergency_round_min',
];

/** Mirror of the server engine — preview only. The server always recomputes. */
function billed(actual: number, kind: 'travel' | 'site', ct: 'standard' | 'emergency', r: Payload['resolved']) {
  if (!actual || actual <= 0) return 0;
  const rule = r[ct][kind];
  return Math.max(Math.ceil(actual / rule.round) * rule.round, rule.min);
}

export default function BillingRulesSettings() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.is_superadmin || (user.roles || []).some((r: any) =>
    ['Admin', 'SuperAdmin'].includes(typeof r === 'string' ? r : r?.name)));

  const [data, setData] = useState<Payload | null>(null);
  const [form, setForm] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ t: 'success' | 'error'; m: string } | null>(null);
  const [probe, setProbe] = useState(35);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/billing-rules');
      setData(r.data);
      setForm({ ...r.data.settings });
    } catch (e: any) {
      setMsg({ t: 'error', m: e?.response?.data?.detail || 'Could not load billing rules' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const body: Record<string, any> = {};
      NUM_KEYS.forEach((k) => { body[k] = Number(form[k]); });
      body.billing_round_up_only = String(form.billing_round_up_only) === 'true';
      body.billing_default_call_type = form.billing_default_call_type;
      // v2.3.5 — MILEAGE
      body.billing_mileage_enabled = String(form.billing_mileage_enabled) === 'true';
      body.billing_mileage_rate_per_mile = Number(form.billing_mileage_rate_per_mile);
      // v2.3.6 — RATES / PHONE REPAIR / ON CALL
      body.billing_labor_rate_per_hour = Number(form.billing_labor_rate_per_hour);
      body.billing_travel_rate_per_hour = Number(form.billing_travel_rate_per_hour);
      body.billing_phone_enabled = String(form.billing_phone_enabled) === 'true';
      body.billing_phone_rate_per_hour = Number(form.billing_phone_rate_per_hour);
      body.billing_phone_free_minutes = Number(form.billing_phone_free_minutes);
      body.billing_phone_bill_mode = form.billing_phone_bill_mode;
      body.billing_phone_round_min = Number(form.billing_phone_round_min);
      body.billing_oncall_enabled = String(form.billing_oncall_enabled) === 'true';
      body.billing_oncall_rate_per_hour = Number(form.billing_oncall_rate_per_hour);
      body.billing_oncall_multiplier = Number(form.billing_oncall_multiplier);
      body.billing_oncall_days = form.billing_oncall_days;
      body.billing_oncall_after_clockout = String(form.billing_oncall_after_clockout) === 'true';
      body.billing_oncall_holidays = String(form.billing_oncall_holidays) === 'true';
      const r = await api.put('/billing-rules', body);
      setData(r.data);
      setForm({ ...r.data.settings });
      setMsg({ t: 'success', m: 'Billing rules saved. They apply to every new leg and call.' });
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setMsg({
        t: 'error',
        m: e?.response?.status === 403
          ? 'Admin access required to change billing rules (rejected by the server).'
          : (d || 'Save failed'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  const resolvedPreview: Payload['resolved'] = {
    standard: {
      travel: { round: Number(form.billing_standard_travel_round_min), min: Number(form.billing_standard_travel_min_min) },
      site: { round: Number(form.billing_standard_site_round_min), min: Number(form.billing_standard_site_min_min) },
    },
    emergency: {
      travel: { round: Number(form.billing_emergency_round_min), min: Number(form.billing_emergency_travel_min_min) },
      site: { round: Number(form.billing_emergency_round_min), min: Number(form.billing_emergency_site_min_min) },
    },
  };

  const mileageRate = Number(form.billing_mileage_rate_per_mile);
  const mileageBad = !(mileageRate >= 0 && mileageRate <= 100) || Number.isNaN(mileageRate);

  const numField = (k: string) => {
    const [lo, hi] = data.ranges[k] || [1, 480];
    const v = form[k];
    const bad = !v || Number(v) <= 0 || Number(v) < lo || Number(v) > hi;
    return (
      <TextField
        key={k}
        label={data.labels[k]}
        type="number"
        size="small"
        fullWidth
        disabled={!isAdmin}
        value={v ?? ''}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        error={bad}
        helperText={bad ? `Must be ${lo}–${hi} minutes` : `minutes · default ${data.defaults[k]} · allowed ${lo}–${hi}`}
        InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">min</Typography> }}
        sx={{ mb: 2 }}
      />
    );
  };

  return (
    <Box>
      {!isAdmin && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 2 }}>
          <strong>Read-only.</strong> Billing rules are system-wide and can only be changed by an
          Admin. Technicians and dispatchers cannot change them — the server rejects the write
          (403) even if the controls were enabled.
        </Alert>
      )}
      {msg && <Alert severity={msg.t} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.m}</Alert>}
      {data.warnings?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.warnings.map((w) => <div key={w}>{w}</div>)}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>How time is billed:</strong> {data.formula}. Rounding is <strong>up only</strong>;
        the minimum is a floor applied <strong>after</strong> rounding. Minimums apply
        <strong> per travel leg and per call</strong> — not per day.
      </Alert>

      {/* ---------------- RATES (v2.3.6) ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="rates-card">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Rates <Chip size="small" color="primary" label="every ERPNext item this engine prices" sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These are the rates the invoice worker puts on the line when the ticket's own time
            entry carries no explicit rate. Each one maps to a real ERPNext Item.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Labor — LABOR-STANDARD ($/hr)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_labor_rate_per_hour ?? ''}
              onChange={(e) => setForm({ ...form, billing_labor_rate_per_hour: e.target.value })}
              helperText={`default ${data.defaults.billing_labor_rate_per_hour} · UOM Hour`}
              inputProps={{ step: '0.01', min: 0, 'data-testid': 'labor-rate' }}
            />
            <TextField
              label="Travel — TRAVEL-TIME ($/hr)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_travel_rate_per_hour ?? ''}
              onChange={(e) => setForm({ ...form, billing_travel_rate_per_hour: e.target.value })}
              helperText={`default ${data.defaults.billing_travel_rate_per_hour} · UOM Hour`}
              inputProps={{ step: '0.01', min: 0, 'data-testid': 'travel-rate' }}
            />
            <TextField
              label="Mileage — MILEAGE ($/mile)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_mileage_rate_per_mile ?? ''}
              onChange={(e) => setForm({ ...form, billing_mileage_rate_per_mile: e.target.value })}
              helperText={`default ${data.defaults.billing_mileage_rate_per_mile} · UOM Mile`}
              inputProps={{ step: '0.01', min: 0 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* ---------------- PHONE REPAIR (v2.3.6) ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="phone-card">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Phone repair <Chip size="small" color="info" label="TECH-PHONE · UOM Hour" sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A ticket can be resolved <strong>by phone</strong>, with no truck roll. The first{' '}
            <strong>{form.billing_phone_free_minutes ?? 15} minutes</strong> of the ticket's{' '}
            <strong>total</strong> phone time — accumulated across every call, not per call — are
            free. Past that it bills on its own <code>TECH-PHONE</code> line.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Tech phone rate ($/hr)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_phone_rate_per_hour ?? ''}
              onChange={(e) => setForm({ ...form, billing_phone_rate_per_hour: e.target.value })}
              helperText={`default ${data.defaults.billing_phone_rate_per_hour}`}
              inputProps={{ step: '0.01', min: 0, 'data-testid': 'phone-rate' }}
            />
            <TextField
              label="Free phone time (minutes)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_phone_free_minutes ?? ''}
              onChange={(e) => setForm({ ...form, billing_phone_free_minutes: e.target.value })}
              helperText={`default ${data.defaults.billing_phone_free_minutes} min · 0 = bill from the first minute`}
              inputProps={{ min: 0, max: 480, 'data-testid': 'phone-free' }}
            />
            <FormControl size="small" fullWidth disabled={!isAdmin}>
              <InputLabel>Past the threshold, bill</InputLabel>
              <Select
                label="Past the threshold, bill"
                value={form.billing_phone_bill_mode ?? 'excess'}
                onChange={(e) => setForm({ ...form, billing_phone_bill_mode: e.target.value })}
                inputProps={{ 'data-testid': 'phone-mode' }}
              >
                <MenuItem value="excess">excess — only the time ABOVE the free threshold</MenuItem>
                <MenuItem value="full">full — the WHOLE time once the threshold is crossed</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FormControlLabel
            control={(
              <Switch
                checked={String(form.billing_phone_enabled ?? 'true') === 'true' || form.billing_phone_enabled === true}
                disabled={!isAdmin}
                onChange={(e) => setForm({ ...form, billing_phone_enabled: e.target.checked ? 'true' : 'false' })}
              />
            )}
            label="Bill tech phone time"
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            <strong>Worked example</strong> at {form.billing_phone_free_minutes ?? 15} min free,
            mode <strong>{form.billing_phone_bill_mode ?? 'excess'}</strong>, $
            {form.billing_phone_rate_per_hour ?? 95}/hr:{' '}
            {(() => {
              const free = Number(form.billing_phone_free_minutes ?? 15);
              const rate = Number(form.billing_phone_rate_per_hour ?? 95);
              const mode = String(form.billing_phone_bill_mode ?? 'excess');
              const calc = (t: number) => (t <= free ? 0 : (mode === 'full' ? t : t - free));
              return [10, 15, 20, 45].map((t) => {
                const b = calc(t);
                return `${t} min → ${b} min billable ($${(b / 60 * rate).toFixed(2)})`;
              }).join(' · ');
            })()}
          </Alert>
        </CardContent>
      </Card>

      {/* ---------------- ON CALL (v2.3.6) ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="oncall-card">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            On call <Chip size="small" color="secondary" label="ON-CALL · UOM Hour" sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Work performed <strong>after clock-out</strong>, on one of the days below, or on a{' '}
            <strong>scheduled-off holiday</strong> is automatically classified ON CALL. Its labor
            bills on the <code>ON-CALL</code> item instead of <code>LABOR-STANDARD</code>, so it is
            a distinct line in ERP. Holidays are managed in <strong>Admin → Time Clock</strong>.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="On-call rate ($/hr) — 0 uses the multiplier" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_oncall_rate_per_hour ?? ''}
              onChange={(e) => setForm({ ...form, billing_oncall_rate_per_hour: e.target.value })}
              helperText={`effective: $${(data.on_call?.rate_per_hour ?? 0).toFixed(2)}/hr`}
              inputProps={{ step: '0.01', min: 0, 'data-testid': 'oncall-rate' }}
            />
            <TextField
              label="On-call multiplier (x labor)" type="number" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_oncall_multiplier ?? ''}
              onChange={(e) => setForm({ ...form, billing_oncall_multiplier: e.target.value })}
              helperText={`default ${data.defaults.billing_oncall_multiplier}x`}
              inputProps={{ step: '0.1', min: 1, max: 10, 'data-testid': 'oncall-mult' }}
            />
            <TextField
              label="On-call days (csv)" size="small" fullWidth
              disabled={!isAdmin} value={form.billing_oncall_days ?? ''}
              onChange={(e) => setForm({ ...form, billing_oncall_days: e.target.value })}
              helperText="mon,tue,wed,thu,fri,sat,sun — default sat,sun"
              inputProps={{ 'data-testid': 'oncall-days' }}
            />
          </Stack>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              control={(
                <Switch
                  checked={String(form.billing_oncall_enabled ?? 'true') === 'true' || form.billing_oncall_enabled === true}
                  disabled={!isAdmin}
                  onChange={(e) => setForm({ ...form, billing_oncall_enabled: e.target.checked ? 'true' : 'false' })}
                />
              )}
              label="Bill on-call work distinctly"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={String(form.billing_oncall_after_clockout ?? 'true') === 'true' || form.billing_oncall_after_clockout === true}
                  disabled={!isAdmin}
                  onChange={(e) => setForm({ ...form, billing_oncall_after_clockout: e.target.checked ? 'true' : 'false' })}
                />
              )}
              label="Work after clock-out counts as on call"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={String(form.billing_oncall_holidays ?? 'true') === 'true' || form.billing_oncall_holidays === true}
                  disabled={!isAdmin}
                  onChange={(e) => setForm({ ...form, billing_oncall_holidays: e.target.checked ? 'true' : 'false' })}
                />
              )}
              label="Scheduled-off holidays count as on call"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* ---------------- MILEAGE (v2.3.5) ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="mileage-card">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Mileage <Chip size="small" color="success" label="MILEAGE · UOM Mile" sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Road-snapped travel distance is billed on its own <strong>MILEAGE</strong> line of the
            draft Sales Invoice (qty = miles, UOM Mile), alongside travel time and labor. It is a
            <strong> reimbursement</strong>, so it is <strong>NOT</strong> part of the CA sales-tax
            base (only parts/materials are taxed). A ticket with zero mileage gets no line at all.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label={data.labels.billing_mileage_rate_per_mile || 'Mileage rate ($ per mile)'}
              type="number"
              size="small"
              disabled={!isAdmin || String(form.billing_mileage_enabled) !== 'true'}
              value={form.billing_mileage_rate_per_mile ?? ''}
              onChange={(e) => setForm({ ...form, billing_mileage_rate_per_mile: e.target.value })}
              inputProps={{ step: '0.01', min: 0, max: 100, 'data-testid': 'mileage-rate' }}
              error={mileageBad}
              helperText={mileageBad
                ? 'Must be between 0 and 100 per mile'
                : `$ per mile · default ${data.defaults.billing_mileage_rate_per_mile}`}
              InputProps={{ startAdornment: <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>$</Typography> }}
              sx={{ minWidth: 260 }}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={String(form.billing_mileage_enabled) === 'true'}
                  disabled={!isAdmin}
                  onChange={(e) => setForm({ ...form, billing_mileage_enabled: e.target.checked ? 'true' : 'false' })}
                />
              )}
              label={data.labels.billing_mileage_enabled || 'Bill per-ticket mileage'}
              sx={{ mt: 1 }}
            />
          </Stack>
          <Alert severity={String(form.billing_mileage_enabled) === 'true' ? 'success' : 'warning'} sx={{ mt: 2 }}>
            {String(form.billing_mileage_enabled) === 'true'
              ? `ON — a completed ticket with mileage adds a MILEAGE line at $${Number(form.billing_mileage_rate_per_mile || 0).toFixed(2)}/mi. 32.4 mi → $${(32.4 * Number(form.billing_mileage_rate_per_mile || 0)).toFixed(2)}.`
              : 'OFF — mileage is still recorded on the ticket and the invoice remarks, but no MILEAGE line is added.'}
          </Alert>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Standard call <Chip size="small" label="standard" sx={{ ml: 1 }} />
            </Typography>
            {numField('billing_standard_travel_round_min')}
            {numField('billing_standard_travel_min_min')}
            <Divider sx={{ my: 1 }} />
            {numField('billing_standard_site_round_min')}
            {numField('billing_standard_site_min_min')}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Emergency call <Chip size="small" color="error" label="emergency" sx={{ ml: 1 }} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              A ticket is an emergency call when its type is Emergency (the portal's emergency
              request), or when dispatch sets the call type explicitly.
            </Typography>
            <Box sx={{ mt: 2 }}>
              {numField('billing_emergency_round_min')}
              {numField('billing_emergency_travel_min_min')}
              {numField('billing_emergency_site_min_min')}
            </Box>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Global</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  disabled={!isAdmin}
                  checked={String(form.billing_round_up_only) === 'true'}
                  onChange={(e) => setForm({ ...form, billing_round_up_only: e.target.checked ? 'true' : 'false' })}
                />
              }
              label="Round up only (never to the nearest interval)"
            />
            <FormControl size="small" sx={{ minWidth: 260 }} disabled={!isAdmin}>
              <InputLabel>Default call type for new tickets</InputLabel>
              <Select
                label="Default call type for new tickets"
                value={form.billing_default_call_type || 'standard'}
                onChange={(e) => setForm({ ...form, billing_default_call_type: e.target.value })}
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>What this bills</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Preview with the values above (unsaved changes included).
          </Typography>
          <TextField
            label="Actual time" type="number" size="small" value={probe}
            onChange={(e) => setProbe(Number(e.target.value))}
            InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">min</Typography> }}
            sx={{ mb: 2, width: 200 }}
          />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Call type</strong></TableCell>
                <TableCell><strong>Leg</strong></TableCell>
                <TableCell align="right"><strong>Actual</strong></TableCell>
                <TableCell align="right"><strong>Rounds to</strong></TableCell>
                <TableCell align="right"><strong>Minimum</strong></TableCell>
                <TableCell align="right"><strong>Billed</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(['standard', 'emergency'] as const).map((ct) =>
                (['travel', 'site'] as const).map((kind) => (
                  <TableRow key={`${ct}-${kind}`}>
                    <TableCell>{ct}</TableCell>
                    <TableCell>{kind === 'site' ? 'on site' : 'travel'}</TableCell>
                    <TableCell align="right">{probe} min</TableCell>
                    <TableCell align="right">{resolvedPreview[ct][kind].round} min</TableCell>
                    <TableCell align="right">{resolvedPreview[ct][kind].min} min</TableCell>
                    <TableCell align="right">
                      <Chip size="small" color="primary"
                            label={`${billed(probe, kind, ct, resolvedPreview)} min`} />
                    </TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
                disabled={!isAdmin || saving}>
          {saving ? 'Saving…' : 'Save billing rules'}
        </Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={saving}>
          Reload
        </Button>
      </Stack>
    </Box>
  );
}
